export type ScriptLoadTiming = 'afterInteractive' | 'beforeInteractive';

export type ParsedScriptInclude =
  | { kind: 'external'; src: string; attributes: Record<string, string> }
  | { kind: 'inline'; code: string }
  | { kind: 'meta'; attributes: Record<string, string> };

/** HTML attribute string on `<script ...>` → lowercase keys, empty string for boolean attrs */
export function parseScriptOpeningTagAttributes(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const s = attrString.trim();
  let i = 0;

  while (i < s.length) {
    while (i < s.length && /\s/.test(s[i]!)) i += 1;
    if (i >= s.length) break;

    const nameStart = i;
    while (i < s.length && !/[\s=]/.test(s[i]!)) i += 1;
    const name = s.slice(nameStart, i).toLowerCase();
    if (!name) {
      i += 1;
      continue;
    }

    while (i < s.length && /\s/.test(s[i]!)) i += 1;

    if (i >= s.length || s[i] !== '=') {
      attrs[name] = '';
      continue;
    }

    i += 1;
    while (i < s.length && /\s/.test(s[i]!)) i += 1;

    let value = '';
    const q = s[i];
    if (q === '"' || q === "'") {
      i += 1;
      const valueStart = i;
      while (i < s.length && s[i] !== q) i += 1;
      value = s.slice(valueStart, i);
      if (i < s.length) i += 1;
    } else {
      const valueStart = i;
      while (i < s.length && !/\s/.test(s[i]!)) i += 1;
      value = s.slice(valueStart, i);
    }

    attrs[name] = value;
  }

  return attrs;
}

/**
 * Turn pasted storefront script snippets into injectable parts.
 * Supports full `<script src="...">` tags (e.g. Cookiebot) and inline `<script>...</script>`,
 * plus legacy plain JS without a wrapper (treated as inline).
 */
export function parseScriptIncludeContent(input: string): ParsedScriptInclude[] {
  const trimmed = input.replace(/\r\n/g, '\n').trim();
  if (!trimmed) return [];

  const scriptRe = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
  const results: ParsedScriptInclude[] = [];
  let match: RegExpExecArray | null;
  let foundTag = false;

  while ((match = scriptRe.exec(trimmed)) !== null) {
    foundTag = true;
    const attrPart = match[1] ?? '';
    const body = (match[2] ?? '').trim();
    const attrs = parseScriptOpeningTagAttributes(attrPart);
    const src = attrs.src?.trim();

    if (src) {
      const { src: _removed, ...rest } = attrs;
      results.push({ kind: 'external', src, attributes: rest });
    } else if (body) {
      results.push({ kind: 'inline', code: body });
    }
  }

  if (!foundTag) {
    let legacy = trimmed.replace(/^<!--([\s\S]*?)-->\s*/g, '').trim();
    legacy = legacy
      .replace(/<\/script>/gi, '')
      .replace(/<script\b[^>]*>/gi, '')
      .trim();
    if (legacy) {
      results.push({ kind: 'inline', code: legacy });
    }
  }

  return results;
}
